import { Dialog } from '@angular/cdk/dialog';
import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { OiqUiAlertDialogService } from '@oiq-ui-sdk/ui';
import { map, Observable, of, switchMap } from 'rxjs';
import { SaveClientDocumentsDialogComponent } from '../components/save-client-documents-dialog/save-client-documents-dialog.component';
import { EmailAttachment, EmailLinkTarget } from '../email-app.interfaces';
import { EMAIL_APP_STORE } from '../email-app.store';
import { Email } from './email-oauth.service';

@Injectable({ providedIn: 'root' })
export class EmailNewApplicationService {
  private store = inject(EMAIL_APP_STORE);
  private dialog = inject(Dialog);
  private alertService = inject(OiqUiAlertDialogService);
  private datePipe = inject(DatePipe);

  /**
   * Every message of the thread `email` belongs to, newest first. Falls back to the
   * message itself when its thread isn't in the loaded list.
   */
  threadMessages(email: Email): Email[] {
    const thread = this.store.getThreadByEmailId(email.ThreadID || email.ID);
    if (!thread?.Messages?.length) return [email];
    // Thread.Messages is sorted oldest→newest; the UI reads newest first.
    return [...thread.Messages].reverse();
  }

  /**
   * Load the thread's attachments and map them to documents-dialog rows.
   * Emits null when messages failed to load and the user chose not to continue
   * without them — a silently short list would hide documents from the application.
   */
  collectThreadAttachments(email: Email): Observable<EmailAttachment[] | null> {
    return this.store.fetchMessagesAttachments(this.threadMessages(email)).pipe(
      switchMap(({ loaded, failed }) => {
        if (failed.length === 0) return of(loaded);

        return this.alertService
          .confirm(
            {
              title: 'Some attachments could not be loaded',
              content: `${failed.length} of ${failed.length + loaded.length} messages with attachments failed to load. Continue without them?`,
              type: 'warning',
            },
            {}
          )
          .pipe(map(confirmed => (confirmed ? loaded : null)));
      }),
      map(messages => (messages ? this.buildAttachments(messages) : null))
    );
  }

  /**
   * Open the documents dialog in New Application mode. `linkTarget` is thread-level
   * even though the attachments come from individual messages.
   */
  openDocumentsDialog(
    attachments: EmailAttachment[],
    context: { emailId: string; linkTarget: EmailLinkTarget; clientId?: number | string }
  ): void {
    this.dialog.open(SaveClientDocumentsDialogComponent, {
      panelClass: ['ocr-upload-dialog', 'email-documents-dialog-left'],
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      disableClose: true,
      data: {
        attachments,
        mode: 'new-application',
        emailId: context.emailId,
        provider: this.store.currentProvider() ?? 'outlook',
        linkTarget: context.linkTarget,
        clientId: context.clientId,
      },
    });
  }

  /**
   * Flatten a thread's attachments into dialog rows. Files are never deduplicated —
   * the same filename in two replies may well be two different documents — so the id
   * has to be unique per message and slot: it keys checkbox state, OCR status and previews.
   */
  private buildAttachments(messages: Email[]): EmailAttachment[] {
    return messages.flatMap(message =>
      (message.Attachments || []).map((a, index) => ({
        name: a.Filename,
        filename: a.Filename,
        contentType: a.ContentType,
        data: a.Data,
        size: a.Size,
        id: `${message.ID}::${index}::${a.Filename}`,
        emailId: message.ID,
        sourceLabel: this.messageSourceLabel(message),
      }))
    );
  }

  /** "address · date, time" heading used to group a thread-wide attachment list by message. */
  private messageSourceLabel(message: Email): string {
    const from = message.From || '';
    const match = from.match(/^(.+?)\s*<(.+?)>$/);
    // Always the address: `From` arrives as "Name <address>" from some senders and as
    // a bare address from others, so taking the display name made the headings switch
    // between a person's name and an email within the same thread.
    const sender = (match ? match[2] : from).trim();
    return [sender || from, this.formatMessageDate(message.Date)].filter(Boolean).join(' · ');
  }

  /**
   * Same "Aug 4, 2026, 3:42 PM" shape the thread message header uses — several
   * messages of a thread can share a day, so the time is what tells them apart.
   */
  private formatMessageDate(value: string): string {
    try {
      return this.datePipe.transform(value, 'MMM d, y · h:mm a') ?? '';
    } catch {
      return '';
    }
  }
}
